import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getVideoLessonQuestions, transformBackendQuestions, submitQuestionAnswer } from '../../../lib/questionsApi';
import { trackQuizCompleted, trackLessonCompleted } from '../../../lib/analytics';
import { getVideoLessonProgress } from '../../../lib/videoLessonApi';
import css from './QuizDesign/quizDesign.module.css';
import QuizSidebar from './QuizDesign/QuizSidebar';
import QuizCompletion from './QuizDesign/QuizCompletion';
import { CheckIcon, XIcon, ArrowIcon, BookIcon } from './QuizDesign/QuizIcons';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

const QuestionCardNew = ({
    videoLessonId,
    onTestComplete,
    onActivityComplete,
    maxAttempts = 2,
    enabledTestTypes = {
        yesNo: true,
        textVoice: true,
        dragDrop: true,
        translationMatch: true
    },
    initialQuestions = null
}) => {
    const { t } = useTranslation();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [results, setResults] = useState([]); // array of true/false/undefined
    const [answered, setAnswered] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [isRight, setIsRight] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);
    const [animClass, setAnimClass] = useState('');
    const [textAnswer, setTextAnswer] = useState('');
    const cardRef = useRef(null);

    useEffect(() => {
        const fetchQuestions = async () => {
            // Use initial questions if provided (e.g. from LessonBlock to avoid extra API calls)
            if (initialQuestions && initialQuestions.length > 0) {
                console.log('[QuestionCardNew] Using initialQuestions from props');
                const transformed = transformBackendQuestions(initialQuestions);
                setQuestions(transformed.slice(0, 10)); // max 10
                setLoading(false);
                return;
            }

            if (!videoLessonId) {
                console.warn('[QuestionCardNew] No videoLessonId provided');
                setLoading(false);
                return;
            }
            try {
                console.log('[QuestionCardNew] Fetching questions for videoLessonId:', videoLessonId);
                const data = await getVideoLessonQuestions(videoLessonId, 'translation_match');
                console.log('[QuestionCardNew] API response:', data);
                const raw = data.questions || data.results || data;
                console.log('[QuestionCardNew] Raw questions:', raw);
                const transformed = transformBackendQuestions(Array.isArray(raw) ? raw : []);
                console.log('[QuestionCardNew] Transformed questions:', transformed);
                setQuestions(transformed.slice(0, 10)); // max 10
                setLoading(false);
            } catch (err) {
                console.error("[QuestionCardNew] Failed to load questions", err);
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [videoLessonId, initialQuestions]);

    const totalQuestions = questions.length;
    const current = questions[currentIndex];

    // Determine quiz type label for sidebar
    const getQuizTypeLabel = (q) => {
        if (!q) return '';
        switch (q.type) {
            case 'translationMatch': return 'Word meaning';
            case 'yesNo': return 'True / False';
            case 'textVoice': return 'Open Answer';
            case 'dragDrop': return 'Fill in the blank';
            default: return 'Quiz';
        }
    };

    const handlePickTranslation = useCallback(async (idx) => {
        if (answered || submitting) return;
        setSelectedIdx(idx);
        setSubmitting(true);

        const q = questions[currentIndex];
        const selectedText = q.answerOptions[idx];

        try {
            const res = await submitQuestionAnswer(q.id, selectedText, 'translationMatch');
            const correct = res.result === 'correct' || res.answer_status === 'RIGHT';

            setIsRight(correct);
            if (correct) setScore(s => s + 1);

            const newResults = [...results];
            newResults[currentIndex] = correct;
            setResults(newResults);

            setFeedback(res.feedback || res.ai_feedback || '');
            setAnswered(true);
        } catch (err) {
            console.error("Failed to submit answer", err);
            // On error, treat as wrong
            setIsRight(false);
            const newResults = [...results];
            newResults[currentIndex] = false;
            setResults(newResults);
            setFeedback(err.message || "Error submitting answer");
            setAnswered(true);
        } finally {
            setSubmitting(false);
        }
    }, [answered, submitting, questions, currentIndex, results]);

    const handleAnswerYesNo = useCallback(async (val) => {
        if (answered || submitting) return;
        setSelectedIdx(val);
        setSubmitting(true);

        const q = questions[currentIndex];

        try {
            const res = await submitQuestionAnswer(q.id, val, 'yesNo');
            const correct = res.result === 'correct' || res.answer_status === 'RIGHT';

            setIsRight(correct);
            if (correct) setScore(s => s + 1);

            const newResults = [...results];
            newResults[currentIndex] = correct;
            setResults(newResults);

            setFeedback(res.feedback || res.ai_feedback || '');
            setAnswered(true);
        } catch (err) {
            console.error("Failed to submit answer", err);
            setIsRight(false);
            const newResults = [...results];
            newResults[currentIndex] = false;
            setResults(newResults);
            setFeedback(err.message || "Error submitting answer");
            setAnswered(true);
        } finally {
            setSubmitting(false);
        }
    }, [answered, submitting, questions, currentIndex, results]);

    const handleSubmitText = useCallback(async () => {
        if (answered || submitting || !textAnswer.trim()) return;
        setSubmitting(true);

        const q = questions[currentIndex];

        try {
            const res = await submitQuestionAnswer(q.id, textAnswer, q.type);
            const correct = res.result === 'correct' || res.answer_status === 'RIGHT';

            setIsRight(correct);
            if (correct) setScore(s => s + 1);

            const newResults = [...results];
            newResults[currentIndex] = correct;
            setResults(newResults);

            setFeedback(res.feedback || res.ai_feedback || '');
            setAnswered(true);
        } catch (err) {
            console.error("Failed to submit answer", err);
            setIsRight(false);
            const newResults = [...results];
            newResults[currentIndex] = false;
            setResults(newResults);
            setFeedback(err.message || "Error submitting answer");
            setAnswered(true);
        } finally {
            setSubmitting(false);
        }
    }, [answered, submitting, textAnswer, questions, currentIndex, results]);

    const handleNext = useCallback(() => {
        if (!answered) return;

        if (currentIndex >= totalQuestions - 1) {
            setShowCompletion(true);
            if (onTestComplete) {
                onTestComplete({
                    score,
                    total: totalQuestions,
                    percentage: Math.round((score / totalQuestions) * 100)
                });
            }
            return;
        }

        setAnimClass(css.qCardAnimOut);
        setTimeout(() => {
            setCurrentIndex(i => i + 1);
            setAnswered(false);
            setSelectedIdx(null);
            setIsRight(null);
            setFeedback(null);
            setTextAnswer('');
            setAnimClass(css.qCardAnimIn);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setAnimClass(''));
            });
        }, 200);
    }, [answered, currentIndex, totalQuestions, onTestComplete, score]);

    const handleReview = () => {
        setShowCompletion(false);
        setCurrentIndex(0);
        setAnswered(false);
        setSelectedIdx(null);
        setIsRight(null);
        setFeedback(null);
        setScore(0);
        setResults([]);
        setTextAnswer('');
    };

    const handleContinue = () => {
        if (onActivityComplete) {
            onActivityComplete(Math.round((score / totalQuestions) * 10));
        } else if (window.advanceStep) {
            window.advanceStep();
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className={css.quizWrap}>
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>
                    {t("Loading...")}
                </div>
            </div>
        );
    }

    // No questions
    if (questions.length === 0) {
        return (
            <div className={css.quizWrap}>
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>
                    {t("No questions available.")}
                </div>
            </div>
        );
    }

    // Completion screen
    if (showCompletion) {
        return (
            <div className={css.quizWrap}>
                <QuizCompletion
                    score={score}
                    totalQuestions={totalQuestions}
                    onContinue={handleContinue}
                    onReview={handleReview}
                />
            </div>
        );
    }

    // Find the correct option index (after answer) for Translation Match highlighting
    const findCorrectIdx = () => {
        if (!current || current.type !== 'translationMatch' || !feedback) return -1;
        // The first option in answer_options is the correct one per API spec
        // But we rely on backend feedback, so we just highlight the user's selection
        return -1;
    };

    return (
        <div className={css.quizWrap}>
            {/* Progress bar */}
            <div className={css.flowBar}>
                <div className={css.flowProgress}>
                    <span className={css.flowQ}>
                        {currentIndex + 1} / {totalQuestions}
                    </span>
                    <div className={css.progWrap}>
                        <div
                            className={css.progFill}
                            style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            <div className={css.quizLayout}>
                {/* Main quiz area */}
                <div className={css.quizMain} style={{ flex: 1, minWidth: 0 }}>

                    {/* ======= TRANSLATION MATCH (Word Meaning) ======= */}
                    {current.type === 'translationMatch' && (
                        <>
                            {/* Question card */}
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge}>
                                    <BookIcon style={{ width: 13, height: 13 }} />
                                    Word meaning
                                </div>
                                <div className={css.qInstr}>{t('Choose the correct meaning of the word.')}</div>
                                <div className={css.qWordWrap}>
                                    <div className={css.qWord}>{current.text}</div>
                                    <div className={css.qMeans}>{t('means')}:</div>
                                </div>
                            </div>

                            {/* Options (A, B, C) */}
                            <div className={css.qOptsVertical}>
                                {current.answerOptions.map((opt, i) => {
                                    let optClass = css.qOpt;
                                    let letterClass = css.qOptLetter;
                                    let textClass = css.qOptText;
                                    let icon = null;

                                    if (answered) {
                                        if (i === selectedIdx) {
                                            if (isRight) {
                                                optClass += ` ${css.qOptCorrect}`;
                                                letterClass = css.qOptLetterCorrect;
                                                textClass = css.qOptTextCorrect;
                                                icon = <span className={`${css.qOptIcoShow} ${css.icoOk}`}><CheckIcon strokeWidth={3} /></span>;
                                            } else {
                                                optClass += ` ${css.qOptWrong}`;
                                                letterClass = css.qOptLetterWrong;
                                                textClass = css.qOptTextWrong;
                                                icon = <span className={`${css.qOptIcoShow} ${css.icoNo}`}><XIcon /></span>;
                                            }
                                        } else {
                                            optClass += ` ${css.qOptDisabled}`;
                                        }
                                    }

                                    return (
                                        <div
                                            key={i}
                                            className={optClass}
                                            onClick={() => handlePickTranslation(i)}
                                            style={answered || submitting ? { cursor: 'default' } : {}}
                                        >
                                            <span className={letterClass}>{LETTERS[i]}</span>
                                            <span className={textClass}>{opt}</span>
                                            {icon}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ======= YES/NO (True/False) ======= */}
                    {current.type === 'yesNo' && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge} style={{ background: 'var(--i50)', color: 'var(--i500)' }}>
                                    True / False
                                </div>
                                <div className={css.qInstr}>{t("Determine if the statement is correct.")}</div>
                                <div className={css.qText}>{current.text}</div>
                            </div>

                            <div className={css.qOptsTF}>
                                {['yes', 'no'].map((val) => {
                                    let optClass = css.qOptTF;
                                    if (answered) {
                                        if (selectedIdx === val) {
                                            optClass += isRight ? ` ${css.qOptCorrect}` : ` ${css.qOptWrong}`;
                                        } else {
                                            optClass += ` ${css.qOptDisabled}`;
                                        }
                                    }

                                    return (
                                        <div
                                            key={val}
                                            className={optClass}
                                            onClick={() => handleAnswerYesNo(val)}
                                            style={answered || submitting ? { cursor: 'default' } : {}}
                                        >
                                            <span className={css.qOptLabel}>{val === 'yes' ? '✓' : '✗'}</span>
                                            <span className={css.qOptText}>{val === 'yes' ? 'True' : 'False'}</span>
                                            {answered && selectedIdx === val && (
                                                isRight
                                                    ? <span className={`${css.qOptIcoShow} ${css.icoOk}`}><CheckIcon strokeWidth={3} /></span>
                                                    : <span className={`${css.qOptIcoShow} ${css.icoNo}`}><XIcon /></span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ======= TEXT/VOICE & DRAG-DROP (Open Answer) ======= */}
                    {(current.type === 'textVoice' || current.type === 'dragDrop') && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge} style={{ background: '#FFF1F2', color: '#E11D48' }}>
                                    Open Answer
                                </div>
                                <div className={css.qInstr}>{t("Answer the question.")}</div>
                                <div className={css.qText}>{current.text}</div>
                            </div>

                            <div style={{ marginTop: 0 }}>
                                <textarea
                                    value={textAnswer}
                                    onChange={e => setTextAnswer(e.target.value)}
                                    disabled={answered || submitting}
                                    placeholder={t("Type your answer here...")}
                                    style={{
                                        width: '100%', minHeight: '100px', padding: '16px',
                                        borderRadius: '16px', border: '2px solid #E5E7EB',
                                        fontSize: '16px', fontFamily: "'Sora', sans-serif", outline: 'none',
                                        background: '#fff', resize: 'vertical',
                                        transition: 'border-color .2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6366F1'}
                                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                                />
                            </div>
                            {!answered && (
                                <div className={css.qAct}>
                                    <button
                                        className={textAnswer.trim() ? css.qBtnAct : css.qBtnDis}
                                        onClick={handleSubmitText}
                                        disabled={!textAnswer.trim() || submitting}
                                    >
                                        {submitting ? t("Checking...") : t("Submit answer")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* ======= FEEDBACK (all types) ======= */}
                    {answered && (
                        <div className={`${css.qFb} ${css.qFbShow} ${isRight ? css.qFbOk : css.qFbNo}`}>
                            <div className={isRight ? css.qFbTitleOk : css.qFbTitleNo}>
                                {isRight ? t('Correct!') : t('Not quite')}
                            </div>
                            <div className={css.qFbDesc}>
                                {feedback && (
                                    <>{current.type === 'translationMatch' && (
                                        <>
                                            {isRight ? (
                                                <><strong>{current.text}</strong> = {current.answerOptions[selectedIdx]} · "{feedback}"</>
                                            ) : (
                                                <>{t('Correct answer')}: <strong>{feedback}</strong></>
                                            )}
                                        </>
                                    )}
                                    {current.type !== 'translationMatch' && (
                                        <>{feedback}</>
                                    )}
                                    </>
                                )}
                                {!feedback && isRight && (
                                    <>{t('Great job!')}</>
                                )}
                                {!feedback && !isRight && (
                                    <>{t('Try to review the video material again.')}</>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ======= ACTION BUTTON ======= */}
                    <div className={css.qAct}>
                        {answered ? (
                            <button className={css.qBtnAct} onClick={handleNext}>
                                {currentIndex >= totalQuestions - 1
                                    ? <>{t('Finish quiz')} <CheckIcon strokeWidth={2} style={{ width: 16, height: 16 }} /></>
                                    : <>{t('Next question')} <ArrowIcon style={{ width: 16, height: 16 }} /></>
                                }
                            </button>
                        ) : (
                            current.type !== 'textVoice' && current.type !== 'dragDrop' && (
                                <button className={css.qBtnDis}>
                                    {submitting ? t('Checking...') : t('Choose an answer')}
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <QuizSidebar
                    currentIndex={currentIndex}
                    totalQuestions={totalQuestions}
                    results={results}
                    score={score}
                    quizType={getQuizTypeLabel(current)}
                />
            </div>
        </div>
    );
};

export default QuestionCardNew;
